import { appBrandText } from './brand';
/**
 * Centralized user-facing string messages.
 *
 * Rules:
 * - All user-facing strings (errors, confirmations, labels) must be here.
 * - Components must import from this file — never hardcode strings.
 * - Grouped by domain for maintainability.
 */

// ── Generic ───────────────────────────────────────────────────────────────────
/** Product logo popover + signature (CALC5 R2/R3). `product` = « NeoCalcul »… */
export const PRODUCT_BRAND = {
  label: (product) => `${product} — aller sur NeoProf`,
  tool: (product) => `${product} · un outil NeoProf`,
  madeBy: (author) => `Développé avec ❤️ par ${author}`,
  goTo: (product) => `Aller sur ${product}`,
};

export const GENERIC = {
  ERROR_NETWORK:    'Erreur réseau. Veuillez réessayer.',
  ERROR_SERVER:     'Erreur serveur. Veuillez réessayer.',
  ERROR_UNKNOWN:    'Une erreur inattendue est survenue.',
  LOADING:          'Chargement…',
  SAVING:           'Enregistrement…',
  SUCCESS:          'Opération réussie.',
  CONFIRM_DELETE:   'Êtes-vous sûr de vouloir supprimer cet élément ?',
  COLLAPSE_LABEL:   (title) => `Réduire ${title}`,
  EXPAND_LABEL:     (title) => `Développer ${title}`,
  REQUIRED_FIELD:   (field) => `${field} est obligatoire.`,
};

// ── Auth ─────────────────────────────────────────────────────────────────────
export const AUTH = {
  LOGIN_ERROR:      'Identifiants incorrects.',
  SESSION_EXPIRED:  'Votre session a expiré. Veuillez vous reconnecter.',
  UNAUTHORIZED:     'Accès non autorisé.',
};

// ── Families ──────────────────────────────────────────────────────────────────
export const FAMILIES = {
  CREATE_SUCCESS:   'Famille créée avec succès.',
  UPDATE_SUCCESS:   'Famille mise à jour.',
  DELETE_SUCCESS:   'Famille supprimée.',
  NOT_FOUND:        'Famille introuvable.',
  DUPLICATE:        'Une famille avec ce nom existe déjà.',
};

// ── Students ──────────────────────────────────────────────────────────────────
export const STUDENTS = {
  CREATE_SUCCESS:   'Élève créé avec succès.',
  UPDATE_SUCCESS:   'Élève mis à jour.',
  ARCHIVE_SUCCESS:  'Élève archivé.',
  UNARCHIVE_SUCCESS:'Élève restauré.',
  NOT_FOUND:        'Élève introuvable.',
};

// ── Lessons ───────────────────────────────────────────────────────────────────
export const LESSONS = {
  CREATE_SUCCESS:   'Cours créé avec succès.',
  UPDATE_SUCCESS:   'Cours mis à jour.',
  DELETE_SUCCESS:   'Cours supprimé.',
  NOT_FOUND:        'Cours introuvable.',
};

// ── Payments ──────────────────────────────────────────────────────────────────
export const PAYMENTS = {
  CREATE_SUCCESS:   'Paiement enregistré.',
  DELETE_SUCCESS:   'Paiement supprimé.',
  NOT_FOUND:        'Paiement introuvable.',
};

// ── Invoices ──────────────────────────────────────────────────────────────────
/** Family pickers (invoice, payment): why a family is listed first. */
export const FAMILY_ORDER_COPY = {
  lastLesson: 'dernier cours',
};

export const INVOICES = {
  GENERATE_SUCCESS: 'Facture générée.',
  SEND_SUCCESS:     'Facture envoyée par email.',
  SEND_ERROR:       'Erreur lors de l\'envoi de la facture.',
  DOWNLOAD_ERROR:   'Erreur lors du téléchargement.',
};

// ── Urssaf ────────────────────────────────────────────────────────────────────
export const URSSAF_REAUTH = Object.freeze({
  CODE: 'RECENT_LOGIN_REQUIRED',   // backend routes/urssaf.py RECENT_LOGIN_REQUIRED_CODE
  TITLE: 'Reconnexion nécessaire',
});

export const URSSAF = {
  REGISTER_SUCCESS: 'Famille enregistrée auprès de l\'URSSAF.',
  REGISTER_ERROR:   'Erreur lors de l\'enregistrement URSSAF.',
  SYNC_SUCCESS:     'Statuts de paiement synchronisés.',
};

// ── Tasks ─────────────────────────────────────────────────────────────────────
export const TASKS = {
  CREATE_SUCCESS:   'Tâche créée.',
  UPDATE_SUCCESS:   'Tâche mise à jour.',
  DELETE_SUCCESS:   'Tâche supprimée.',
};

// ── Evaluations ───────────────────────────────────────────────────────────────
export const EVALUATIONS = {
  CREATE_SUCCESS:   'Évaluation enregistrée.',
  UPDATE_SUCCESS:   'Évaluation mise à jour.',
  DELETE_SUCCESS:   'Évaluation supprimée.',
};

// ── Homework ──────────────────────────────────────────────────────────────────
export const HOMEWORK = {
  CREATE_SUCCESS:   'Devoir assigné.',
  UPDATE_SUCCESS:   'Devoir mis à jour.',
  DELETE_SUCCESS:   'Devoir supprimé.',
};

// ── Game ──────────────────────────────────────────────────────────────────────
export const GAME = {
  ACTIVITY_ERROR:   'Impossible de charger l\'activité.',
  IMPORT_SUCCESS:   'Activités importées avec succès.',
};

// ── Adaptive game — the wheel itself (G2) ────────────────────────────────────
// `GameUI` was the last component still holding hardcoded English ("Challenge
// Game", "New Challenge", "Pull the lever…", "Spinning…", "played · left") in
// an app whose every other string is French. The students are French.
export const GAME_UI = {
  TITLE:            'Le défi',
  PULL_CUE:         '↓ TIRE ↓',
  SPINNING:         'Ça tourne…',
  PLACEHOLDER:      'Tire le levier pour découvrir ton défi !',
  RESULT_LABEL:     'Ton défi',
  NEW_CHALLENGE:    'Nouveau défi',
  HISTORY:          (played, left) =>
    `${played} joué${played > 1 ? 's' : ''} · ${left} restant${left > 1 ? 's' : ''}`,
  RESET:            (n) => `Réinitialiser (${n})`,
  CLOSE_LABEL:      'Fermer le jeu',
};

// ── Adaptive game — outcome capture (G2) ──────────────────────────────────────
// The outcome bar shown when a physical / fun / neutral challenge is revealed.
// ⚠ Never name a body part and never surface a family id here.
export const GAME_OUTCOME = {
  PROMPT:           'Alors ?',
  SUCCESS:          'Réussi',
  FAIL:             'Raté',
  SKIP:             'Pas joué',
  SUCCESS_LABEL:    'Défi réussi',
  FAIL_LABEL:       'Défi raté',
  SKIP_LABEL:       'Défi non joué',
  RECORDED:         'Enregistré',
  RECORD_ERROR:     'Résultat en attente. La sauvegarde sera réessayée automatiquement.',
};

// ── Adaptive game — maths challenge (G2) ──────────────────────────────────────
export const EXAM_PANEL = {
  TITLE: 'Examen personnalisé',
  STEP_NOTIONS: '1. Choisir les notions',
  STEP_COUNT: '2. Choisir le nombre de questions',
  STEP_LEVELS: '3. Choisir les niveaux',
  CALCUL_LINK: 'Créer un lien d’entraînement Calcul',
  MINIMUM: (count) => `Minimum : ${count} question${count > 1 ? 's' : ''}, une par compétence.`,
  DECREASE: 'Retirer une question',
  INCREASE: 'Ajouter une question',
  DECREASE_MARK: '−',
  INCREASE_MARK: '+',
  CLOSE: 'Fermer l’examen personnalisé',
  CLOSE_MARK: '×',
  COUNT: 'Nombre de questions',
  LEVELS: 'Niveaux sélectionnés',
  LEVEL: (level) => `Niveau ${level}`,
  BACK: 'Retour',
  NEXT: 'Continuer',
  START: 'Commencer l’examen',
  BUILDING: 'Préparation…',
};

// Answer widgets (MRV1 A3): set, coordinates, helper keys.
export const ANSWER_WIDGET_LABELS = {
  SET_VALUE: (index) => `x${['₁', '₂', '₃'][index]} =`,
  SINGLE_VALUE: 'x =',
  COORDINATE: (name) => `${name} =`,
  ONE_SOLUTION: 'Une seule solution',
  TWO_SOLUTIONS: 'Deux solutions',
  NO_SOLUTION: 'Aucune solution',
  KEYS: 'Symboles',
  KEY: (key) => `Insérer ${key}`,
};

export const GAME_CALC = {
  LOAD_ERROR: 'Impossible de préparer ou corriger la question. Réessaie avec Valider.',
  RETRY:            'Essaie encore',
  ERROR_HINT:       'Indice :',
  DONT_KNOW:        'Je ne sais pas',
  DONT_KNOW_LABEL:  'Révéler la réponse',
  ANSWER_WAS:       'La réponse était',
  ATTEMPTS:         (n) => `${n} essai${n > 1 ? 's' : ''}`,
  RANK_UP:          (rank) => `Niveau ${rank} débloqué !`,
  DONE:             (n) => `Tu as réussi ${n} calcul${n > 1 ? 's' : ''} !`,
  // The whole weighted chapter-detection system was invisible: the server
  // resolved a chapter, aimed the questions at it, and told nobody. A student
  // revising Pythagore saw three geometry questions and no reason to believe
  // they had anything to do with the lesson.
  CHAPTER:          (label) => `Chapitre : ${label}`,
  CHAPTER_LABEL:    (label) => `Ce défi porte sur le chapitre ${label}`,
  // ⚠ Teacher-side verification only — never rendered on a public practice link.
  SOURCE_BANK:      'question du chapitre',

  // ── The rebuilt screen (DEFI2 / T-DEFI2.5) ──
  // ⚠ EVERY string the maths screen renders lives here, aria-labels included
  // (invariant 10). Before this the component shipped
  // `lang === 'fr' ? 'Ta reponse...' : 'Your answer...'` and seven more like it
  // inline, which broke that rule in eight places and left un-accented copy on
  // the loudest text on the screen.
  //
  // The chapter is the HEADLINE of this screen, not a chip — the mock-ups' one
  // unambiguously right decision. When no chapter was detected the mode's own
  // title takes the headline rather than an empty space or an invented chapter
  // (invariant 7).
  SUBTITLE:         'Calcule, réfléchis, progresse !',
  PROGRESS:         (n, total) => `${n}/${total}`,
  PROGRESS_LABEL:   (n, total) => `Question ${n} sur ${total}`,
  ANSWER_LABEL:     'Ta réponse',
  LEAVE:            'Terminer le défi',
  ELAPSED_LABEL:    (mmss) => `Temps écoulé : ${mmss}`,
  WRONG_BANNER:     'FAUX',

  // Statement QCM (T-DEFI2.21 A): the size of the key is announced, never guessed.
  PICK_ALL:         'Coche toutes les bonnes réponses',
  PICK_ONE:         'Une seule bonne réponse',
  VALIDATE:         'Valider',

  // The ending. ⚠ Nothing here is a button: the run is already graded and
  // already reported, so the screen leaves by itself.
  DONE_TITLE:       'Défi terminé',
  DONE_MOVED:       (rank) => `Niveau ${rank}`,
  DONE_UNMOVED:     'Niveau inchangé',

  // The screen's own title when no chapter is shown (was `generators.getModeTitle`).
  MODE_TITLES: {
    multiplication: 'MULTIPLICATIONS & CARRÉS',
    equation:       'ÉQUATIONS',
    percentage:     'POURCENTAGES',
    geometry:       'GÉOMÉTRIE & ALGÈBRE',
    pattern:        'SUITE LOGIQUE',
    memory:         'MÉMOIRE',
    random:         'CALCUL MENTAL',
  },

  // Memory mode.
  MEMORY_MEMORIZE:  'Mémorise cette séquence !',
  MEMORY_TYPE:      'Écris la séquence :',
  MEMORY_PLACEHOLDER: 'Ex : 3 7 2 5 9',
  MEMORY_LABEL:     'La séquence de chiffres',
  MEMORY_COUNTDOWN: (n) => `${n} s`,
  MEMORY_CORRECT:   'Correct !',
  MEMORY_WRONG:     'Pas tout à fait…',
  MEMORY_EXPECTED:  'La séquence était :',
  MEMORY_GIVEN:     'Ta réponse :',
  MEMORY_EMPTY_DIGIT: '—',
  MEMORY_DIGIT_COMPARISON: (position, given, expected) => `Chiffre ${position} : ${given ?? 'manquant'}, attendu ${expected ?? 'aucun'}.`,
  MEMORY_CORRECTION_HINT: 'Les chiffres soulignés diffèrent. — indique un chiffre manquant ou en trop.',
  MEMORY_NEXT:      'Séquence suivante',
  MEMORY_FINISH:    'Terminer le défi',
};

// Headlines shown on the correct / wrong screens. French only: these were the
// last English strings left in the game ("WHAT !!", "GLITCH", "TRY AGAIN",
// "YES !!") and they sat on the biggest, loudest text on the screen.
//
// ⚠ `supernova` is the top of the range (T-DEFI2.7): a correct answer at the
// hardest rung the ladder has. « RATÉ !! » stays the ceiling on the other side
// and it is about the ANSWER — the game never mocks the child.
export const GAME_CALC_HEADLINE = {
  CORRECT: {
    classic: 'BRAVO !', fireworks: 'OUAIS !!', trophy: 'CHAMPION',
    supernova: 'ÉNORME !!!', comet: 'MAGNIFIQUE !', wink: 'BIEN JOUÉ !',
  },
  WRONG:   {
    classic: 'RATÉ !!', glitch: 'ERREUR', vacuum: 'ESSAIE ENCORE', resolve: 'ON REPART',
  },
};

// ── Adaptive game — engagement layer (G2 / T-G2.9) ────────────────────────────
// ⚠ Only ever shown for `calcul` and `jeu_logique`. The physical families have
// no progression bar and no label — never name a body part.
// ⚠ The wrap is SILENT: reaching the top of a non-maths ladder resets it with
// no message at all, so there is deliberately no "wrap" string here.
export const GAME_PROGRESS = {
  LEVEL:            (rank, max) => `niveau ${rank}/${max}`,
  BAR_LABEL:        (label, rank, max) => `${label} — niveau ${rank} sur ${max}`,
  STREAK:           (n) => `${n} d'affilée`,
};

export const GAME_FAMILY_LABEL = {
  calcul:      'Calcul',
  calcul_mental: 'Calcul mental',
  jeu_logique: 'Logique',
  english:     'Anglais',
};

// ── Le défi — the rebuilt challenge screen ───────────────────────────────────
// ⚠ Every string French, sentence case, no emoji.
// ⚠ Nothing here may name a family id. `sport_a` / `sport_b` are internal and
// never reach a screen; « Sport » below is the domain a child understands, not
// the id, and there is deliberately no key that could hold one.
// ⚠ The humour lives in exactly three places — the mascot's face, the carousel
// overshoot, and this table. Never at a child's expense: the heat line is about
// the pause, not about a pupil, and « RATÉ !! » in GAME_CALC_HEADLINE is the
// ceiling.
export const GAME_DEFI = {
  examBuildError: 'Impossible de préparer cet examen. Réessaie.',
  pickFallback: 'Ce défi est indisponible. Un autre domaine a été proposé.',
  drawError: 'Impossible de préparer ce défi. Réessaie depuis les tuiles.',
  emptyPool: 'Aucune question disponible pour ce défi. Choisis une autre tuile.',
  droppedNotions: (count) => `${count} notion${count > 1 ? 's' : ''} écartée${count > 1 ? 's' : ''} : limite de sélection ou questions indisponibles.`,
  workshopPopupBlocked: 'Le navigateur a bloqué l’atelier. Ouvre-le dans un nouvel onglet.',
  title:             appBrandText('DEFI_EXPRESS'),
  subtitle:          'Prêt pour un petit challenge ?  ^_^',
  pull:              'Tire pour une surprise !',
  orTap:             'ou choisis ton défi',
  yourTurn:          'À toi de jouer !',
  random:            'Surprise',
  calculHref:        '/calcul',
  // The public doors a tile opens. ⚠ Both are ALSO station cards: the tile is a
  // door onto the same board, not a second implementation of it (V12 · V14).
  numberWordsHref:   '/calcul/nombres-en-lettres',

  // ⚠ TILE LABELS ARE DOMAINS A CHILD UNDERSTANDS, NOT FAMILY IDS.
  // « Sport » is the domain; the two internal physical families behind it have
  // no spelling anywhere in the frontend, which is what makes them unleakable.
  // « Maths » and « Calcul » are the same family in its two modes — chapter
  // exercises, and mental arithmetic to refresh the brain.
  tiles: {
    maths:           'Maths',
    exam:            'Examen',
    niveau:          'Test de niveau',
    calcul:          'Défi surpris',
    calculMental:    'Calcul mental',
    // The one tile that covers every calcul board: it opens the station (V14).
    station:         'Calcul station',
    // ⚠ ONE tile, named by the class: Scratch at l'école et au collège, Python
    // au lycée (V13). Never both at once.
    scratch:         'Scratch',
    python:          'Python',
    numberWords:     'Nombres en lettres',
    sport:           'Sport',
    mental:          'Réflexion',
    english:         'Anglais',
    autre:           'Jeux',
  },
  // The one configuration widget every tile opens (V6 · V23). One wording for
  // all of them — a tile that invents its own labels breaks the promise.
  tileConfig: {
    title:           (tile) => `Réglages · ${tile}`,
    openLabel:       (tile) => `Régler ${tile}`,
    close:           'Fermer',
    start:           'Commencer',
    chrono:          'Chrono',
    chronoActive:    'Chrono activé',
    sound:           'Son',
    settings:        'Réglages',
    door:            'Ce que tu veux faire',
    doors: {
      questions:     'Questions',
      workshop:      'Atelier libre',
      // L'atelier est un éditeur de blocs : il n'existe pas en Python.
      workshopScratchOnly: 'Scratch uniquement',
    },
    // Honest: the door exists, the bank behind it does not yet (V7 ingestion).
    doorSoon:        'Bientôt disponible',
    mode:            'Sens',
    numberKind:      'Nombres',
    class:           'Classe',
    session:         'Type d’épreuve',
    length:          'Nombre de défis',
    modes: {
      digitsToWords: 'Chiffres → lettres',
      wordsToDigits: 'Lettres → chiffres',
    },
    numberKinds: {
      entiers:       'Entiers',
      decimaux:      'Décimaux',
    },
    sessions: {
      full:           'Épreuve complète',
      simulation_30:  'Simulation 30 min',
      chapter_review: 'Révision par chapitre',
    },
  },
  boardHint: {
    maths:           'Le chapitre du moment.',
    exam:            'Compose ton évaluation.',
    niveau:          'Découvre ton niveau de maths.',
    calcul:          'Trois calculs tirés pour te surprendre.',
    calculMental:    'Choisis tes opérations, puis entraîne-toi dans la séance.',
    // ⚠ The subtitle LISTS what is inside (V22): a tile that covers ten boards
    // has to say which, or it reads as one more challenge among the others.
    station:         'Tables, opérations, fractions, conversions, priorités, nombres en lettres.',
    sport:           'Bouge et relâche la pression.',
    mental:          'Observe, déduis, trouve.',
    english:         'Joue avec les mots.',
    autre:           'Un jeu léger pour souffler.',
    aleatoire:       'Laisse le hasard choisir.',
    // One tile, two chapters: the class decides which (V13).
    code:            'Lis un programme, trouve ce qu’il affiche.',
    numberWords:     'Écris les nombres en toutes lettres.',
    diplome:         'Les sujets officiels, en conditions d’examen.',
  },

  // The activity's domain, said plainly. Never a family, never a body part.
  categoryLabel: {
    physical:        'Mouvement',
    mental:          'Réflexion',
    fun:             'Jeu',
    neutral:         'Pause',
  },

  // ⚠ Only ever called with a duration the SERVER sent. A challenge nobody gave
  // a length to shows no chip at all rather than a guess.
  minutesChip:       (seconds) => (seconds < 60
    ? `~${seconds} s`
    : `${Math.round(seconds / 60)} min`),
  randomHint:        (label) => `La roue penche vers ${label}`,
  randomHintNone:    'La roue n\'a pas de préférence',
  chapter:           (label) => `Chapitre : ${label}`,
  noChapter:         'Aucun chapitre détecté',
  // ⚠ When nothing is detected the pause is NOT "no chapter": it is the class
  // test, climbing until a skill trips the pupil (T-DEFI2.19 §5). Saying so is
  // the difference between a system that decided and one that gave up.
  diagnostic:        'Test de la classe',
  back:              'Retour aux défis',

  // ── The notion run (T-DEFI2.19) ──
  // ⚠ The type of a notion is never named. « au-dessus de sa classe » is the
  // one honesty the run owes (invariant 7): it is said every time it is true.
  notion: {
    aboveClass:      (className) => `au-dessus de sa classe · ${className}`,
    level:           (n) => `niveau ${n}`,
    continueOn:      (label) => `Tire le levier : on continue sur ${label}`,
    continueTest:    'Tire le levier : on continue le test',
    locked:          (label) => `On s’arrête sur ${label}`,
    classUp:         (className) => `Place au programme de ${className}`,
    summitTitle:     'Tout est maîtrisé !',
    summitBody:      (label) => `${label} : chaque niveau et chaque classe sont passés. On continue pour le plaisir.`,
  },
  mutedChip:         'revient bientôt',
  levelChip:         (n, max) => `niveau ${n}/${max}`,
  counter:           (played, left) => `${played} joués · ${left} restants`,
  runCount:          (played, total) => `${played}/${total}`,
  change:            'Changer',
  verify:            'Vérifier',
  answerPlaceholder: 'Ta réponse…',
  skip:              'Passer',
  newDefi:           'Nouveau défi',
  openStats:         'Statistiques NeoDéfi de l’élève',
  quit:              'Quitter',
  lessonSuffix:      'cours',

  // ── The maths ending (T-DEFI2.15) ──
  // Three questions graded: what was asked, what was answered, what moved.
  // ⚠ Not one number here that the run did not produce.
  recap: {
    title:           'Ta manche',
    subtitle:        (right, total) => `${right} sur ${total}`,
    skillScore:      (label, right, total) => `${label} · ${right}/${total}`,
    asked:           'Question',
    given:           'Ta réponse',
    noAnswer:        'Pas de réponse',
    expected:        (answer) => `La réponse : ${answer}`,
    rankUp:          (from, to) => `Niveau ${from} → ${to}`,
    rankSame:        'Niveau inchangé',
    rankDown:        (from, to) => `Niveau ${from} → ${to}`,
    again:           'Tire le levier pour la suite',
    rowLabel:        (index, total) => `Question ${index} sur ${total}`,
  },

  // ⚠ The whole-class framing rides the CHALLENGE card, not only the
  // scoreboard — it has to be said while the class can still act on it.
  wholeClass:        'Toute la classe joue ensemble !',

  // P4 micro-line only. Teacher-facing, about the pause.
  heat: {
    calm:            'Tranquille.',
    impatient:       'Le sablier s’impatiente.',
    warm:            'Ça chauffe un peu.',
    hot:             'On y retourne ?',
    critical:        'La pause déborde.',
  },

  a11y: {
    dialog:          appBrandText('DEFI_EXPRESS'),
    pickerDialog:    'Choisir une notion pour le défi',
    carousel:        'Choisir un domaine de défi',
    carouselPages:   'Accès direct aux domaines',
    selectBoard:     (label) => `Choisir le défi ${label}`,
    sandglass:       (mmss, state) => `Temps de pause : ${mmss}, ${state}`,
    orb:             'Tire l\'orbe vers la droite pour tirer un défi au sort',
    exit:            'Fermer le défi',
    prev:            'Défi précédent',
    next:            'Défi suivant',
    // The spoken half of `sandglass`. The visible heat line above is a nudge
    // to the teacher and reads badly out of context ("La pause déborde" as the
    // description of a timer); these describe the glass itself.
    state: {
      maths:         'temps de maths',
      calm:          'dans les temps',
      impatient:     'la pause commence à durer',
      warm:          'un peu long',
      hot:           'ça dure',
      critical:      'très au-delà du temps prévu',
    },
  },
};

// ── Curriculum tagging — "Quel chapitre ?" (G2 / T-G2.10) ────────────────────
// Layer 4 of the tagging system, and the one that makes it visibly honest: the
// teacher sees which chapter was detected and can fix it, and the fix is
// remembered for that teacher only, forever.
// ── Public practice links (G2 / stage B5) ────────────────────────────────────
export const GAME_LINK = {
  PLAY:             (label) => `Jouer : ${label}`,
  PENDING:          '(lien créé à l’enregistrement)',
  PENDING_HINT:     'Le lien de jeu sera créé quand la mission sera enregistrée.',
  UNKNOWN_TAG:      (tag) => `Chapitre inconnu : ${tag}`,
  CREATE_ERROR:     'Impossible de créer le lien de jeu.',
};

// ── The public game page (G2 / T-G2.19) ──────────────────────────────────────
// ⚠ This page must work for someone who has never heard of NeoProf. No jargon,
// no reference to lessons or missions they cannot see.
export const PUBLIC_GAME = {
  GREETING:         (name) => `Salut ${name} !`,
  INTRO:            (label) => `Aujourd’hui, on travaille : ${label}`,
  START:            'Commencer',
  AGAIN:            'Encore une série',
  DONE:             'Bien joué !',
  LOADING:          'Chargement…',
  EXPIRED_TITLE:    'Ce lien a expiré',
  EXPIRED_BODY:     'Demande un nouveau lien à ton professeur.',
  NOT_FOUND_TITLE:  'Lien introuvable',
  NOT_FOUND_BODY:   'Vérifie le lien, ou demandes-en un nouveau à ton professeur.',
  ERROR_TITLE:      'Oups',
  ERROR_BODY:       'Quelque chose n’a pas fonctionné. Réessaie dans un instant.',
};

// ── First-lesson placement test (G2 / stage B6) ──────────────────────────────
// ⚠ IT FINDS A LEVEL. IT DOES NOT GRADE. Nothing in this table may become a
// mark out of 20, a percentage, a share of correct answers or the words
// "niveau faible" — the whole output is "here is where we start".
//
// ⚠ The public/privé question is asked ONCE and never shown back as a
// judgement. It shifts the opening rung by one and is then overwritten by the
// student's own answers within three questions.
export const PLACEMENT = {
  // The offer inside the questions step
  OFFER_TITLE:      'Premier cours : on fait un point de départ ?',
  OFFER_FIRST_EVER: 'C’est le premier cours avec cet élève : il n’y a encore ni missions, ni notes, ni contrôles à passer en revue.',
  OFFER_NEW_YEAR:   'C’est le premier cours de l’année : la classe a changé, et les missions du trimestre dernier ne portent plus sur le bon programme.',
  OFFER_BODY:       'Une dizaine de questions pour savoir par où commencer. Ce n’est pas une évaluation : rien n’est noté.',
  OFFER_START:      'Lancer le test',
  OFFER_SKIP:       'Passer, on verra en cours',
  OFFER_UNAVAILABLE: 'Aucun test disponible pour cette matière — les questions habituelles reprennent.',

  // The public / privé question
  SCHOOL_TYPE_TITLE: 'Tu es dans un établissement public ou privé ?',
  SCHOOL_TYPE_HINT:  'Ça sert seulement à choisir la première question. La suite s’ajuste à tes réponses.',
  SCHOOL_TYPE_PUBLIC: 'Public',
  SCHOOL_TYPE_PRIVE:  'Privé',
  SCHOOL_TYPE_SKIP:   'Je préfère ne pas répondre',

  // The test itself
  TITLE:            'Point de départ',
  INTRO:            'Une dizaine de questions, de plus en plus faciles ou difficiles selon tes réponses. Rien n’est noté.',
  START:            'C’est parti',
  PROGRESS:         (position, total) => `Question ${position} sur ${total}`,
  ANSWER_LABEL:     'Ta réponse',
  VALIDATE:         'Valider',
  DONT_KNOW:        'Je ne sais pas',
  NEXT:             'Question suivante',
  LOADING:          'Préparation du test…',
  SUBMITTING:       'Enregistrement…',
  QUIT:             'Arrêter le test',
  QUIT_TITLE:       'Arrêter le test ?',
  QUIT_CONFIRM:     'Les questions déjà répondues ne seront pas enregistrées.',
  QUIT_CANCEL:      'Continuer le test',

  // The end — a starting point, never a verdict
  DONE_TITLE:       'Merci !',
  DONE_BODY:        'On sait par où commencer.',
  SUMMARY_TITLE:    'Point de départ',
  // ⚠ Two lists, and what went through comes FIRST. A pupil reading their own
  // placement should meet what they can do before what they cannot.
  // "à retravailler" — never "échoué", never "non acquis", which are verdicts.
  ACQUIRED_TITLE:   'Compétences acquises',
  ACQUIRED_EMPTY:   'On repartira des bases ensemble.',
  TO_WORK_TITLE:    'Compétences à retravailler',
  TO_WORK_EMPTY:    'Rien à reprendre pour l’instant.',
  DRILL_TO_WORK:    (level) => `Retravailler ces compétences (niveau ${level})`,
  DRILL_ACQUIRED:   (level) => `Aller plus loin (niveau ${level})`,
  // The défi's way on from the level test — never on the public page.
  WORK_SKILLS:      (count) => (count > 1
    ? 'Travailler ces compétences en maths'
    : 'Travailler cette compétence en maths'),
  // ⚠ These four are the FRENCH TWIN of `PLACEMENT_SUMMARY` in
  // `backend/config/placement_defaults.py`, and they exist because the public
  // page (T-G2.27) persists nothing and therefore cannot ask the server for a
  // summary. Same sentences, same rule: a starting point, never a verdict.
  // Change one side, change the other.
  // ⚠ The colon is doing real work — chapter labels carry no article and their
  // gender and number differ, so "on commencera par Fractions" is
  // ungrammatical for most of them. Same reasoning as the Python twin.
  SUMMARY_FOCUS:    (chapters) => `On commencera par : ${chapters}.`,
  SUMMARY_LEVEL:    (rank, top) => `Niveau estimé : ${rank}/${top}.`,
  SUMMARY_NO_GAPS:  'Tout est passé sans accroc : on peut attaquer directement la suite du programme.',
  SUMMARY_UNKNOWN:  'On commencera par revoir les bases ensemble, puis on ajustera au fil des séances.',
  SUMMARY_JOIN:     { separator: ', ', last: ' et ' },
  CLOSE:            'Revenir au cours',
  ERROR:            'Le test n’a pas pu être préparé.',
  SUBMIT_ERROR:     'Les réponses n’ont pas pu être enregistrées.',
  EMPTY:            'Aucune question disponible pour cette classe pour le moment.',
};

// ── The public per-class test (G2 / T-G2.27) ─────────────────────────────────
// ⚠ Like the public game page, this must work for someone who has never heard
// of NeoProf: no jargon, no login prompt, no reference to a lesson or a
// teacher they do not have.
export const PUBLIC_TEST = {
  KICKER:           'DIX QUESTIONS. UN POINT DE DÉPART.',
  TITLE:            (classLevel) => `Test de niveau — ${classLevel}`,
  SUBTITLE:         'Une dizaine de questions pour situer son niveau. Rien n’est enregistré.',
  NOT_FOUND_TITLE:  'Test introuvable',
  NOT_FOUND_BODY:   'Vérifie l’adresse : la matière ou la classe n’existe pas.',
  ERROR_TITLE:      'Oups',
  ERROR_BODY:       'Quelque chose n’a pas fonctionné. Réessaie dans un instant.',
  PRIVACY:          'Aucune réponse n’est conservée : le résultat s’affiche ici et disparaît quand tu fermes la page.',
  AGAIN:            'Recommencer',
  EXCELLENT:        'Excellent !',
  EXCELLENT_FOCUS:  (skill) => `La seule compétence que tu peux encore améliorer est « ${skill} ».`,
  NEXT_CLASS:       (classLevel) => `Tu as le niveau de ${classLevel}. Continue comme ça : tu es prêt pour la suite.`,
};

// ── « Sur quoi porte la pause ? » — the notion picker (T-DEFI2.19) ──────────
// ⚠ A NOTION IS A SKILL, A SECTION, A CHAPTER OR A TOPIC, AND NOT ONE STRING
// BELOW NAMES WHICH. Depth is shown as containment (« 4 compétences »), which a
// teacher reads without being taught the word "section".
// ⚠ `TOPIC_PICKER` is DELETED with the chapter picker. « Rester en mode
// général » went with it: when nothing is detected the pause is the class test,
// not "no chapter", and the automatic choice is the first-class answer.
export const NOTION_PICKER = {
  UNAVAILABLE_SELECTION: 'Notion indisponible',
  TITLE:            'Sur quoi porte la pause ?',
  AUTO_TITLE:       (label) => `Choisi automatiquement : ${label}`,
  AUTO_DIAGNOSTIC:  'Rien de détecté dans la séance : la pause lance le test de la classe.',
  AUTO_ACTION:      'Laisser le système choisir',
  AUTO_SHORT:       'Automatique',
  PINNED:           (label) => `${label} · choisie`,
  // ── Batch select (T-DEFI2.20 D) ──
  PINNED_MANY:      (count) => `${count} notions · choisies`,
  SELECT_LABEL:     (label) => `Ajouter ${label} à la sélection`,
  SELECTED_MARK:    '✓',
  // An empty box reads as a checkbox; a stray « ○ » read as a broken circle.
  UNSELECTED_MARK:  '',
  PLAY_SELECTED:    (count) => `Valider la sélection · ${count} notion${count > 1 ? 's' : ''}`,
  LEVEL:            (level) => `L${level}`,
  LOADING:          'Lecture de la séance…',
  RETRY:            'Réessayer',
  TECHNICAL_ERROR:  'La détection a échoué. Les jeux restent accessibles.',
  SEARCH:           'Chercher une notion',
  SEARCH_HINT:      'trigo, factorisation, %…',
  EMPTY_HINT:       '',
  NO_MATCH:         'Rien dans cette classe ne correspond.',
  // ⚠ What the lesson named comes FIRST and says where it was found. It is the
  // answer nine times out of ten, and it is what turns "trust me" into "here
  // is why".
  DETECTED_TITLE:   'Dans cette séance',
  CURRENT_TITLE:    'En cours',
  RESULTS_TITLE:    'Résultats',
  SOURCE: {
    upcoming_exam:  'contrôle à venir',
    lesson_task:    'tâche de la séance',
    lesson_point:   'point abordé',
    lesson_mission: 'mission',
    recent_mission: 'dernière séance',
    lesson_remarks: 'remarque',
    older_mission:  'mission plus ancienne',
  },
  SKILLS_COUNT:     (n) => `${n} compétence${n > 1 ? 's' : ''}`,
  EXPAND:           (label) => `Voir ce que contient ${label}`,
  COLLAPSE:         (label) => `Replier ${label}`,
  CHOOSE_LABEL:     (label) => `Choisir ${label}`,
  // Browsing another class — for a pupil working below or above their year.
  BROWSE_CLASS:     'Voir une autre classe',
  BROWSE_OWN:       'Sa classe',
  BROWSE_TITLE:     (name) => `Notions de ${name}`,
  BROWSE_LOADING:   'Chargement…',
  BROWSE_ERROR:     'Impossible de charger les notions de cette classe.',
  CLASS_LABEL:      (name) => `Notions de ${name}`,
  // ⚠ HONEST BEFORE VALIDATING. A new notion starts at once; the questions
  // already answered in the running défi are not counted (T-DEFI2.20).
  INTERRUPTS_RUN:   'Valider relance le défi tout de suite sur la nouvelle notion (le défi en cours n’est pas compté).',
  OPEN_LABEL:       (label) => `Changer de notion (actuellement : ${label})`,
  CLOSE:            'Fermer',
  STEPS:            ['Classe', 'Notions', 'Réglages', 'Séance'],
  STEPS_LABEL:      'Étapes de la séance',
  NEXT_STEP:        'Continuer',
  BACK_STEP:        'Retour',
  CLASS_STEP_TITLE: 'Choisis la classe de la séance.',
  NOTION_STEP_TITLE: 'Qu’est-ce que tu veux travailler ?',
  SETTINGS_STEP_TITLE: 'Vérifie les notions choisies.',
  PARAMETERS_STEP_TITLE: 'Personnalise ta séance.',
  CONFIGURE_STEPS: ['Classe', 'Notions', 'Paramètres', 'Partager'],
  SHARE: 'Partager', PLAY: 'Jouer',
  SESSION_STEP_TITLE: 'Ta séance est prête.',
  SLOTS:            (count, max) => `${count} / ${max} notions`,
  TOO_MANY_SELECTED: (max) => `Choisis au maximum ${max} notions.`,
  ALREADY_INCLUDED: 'Déjà inclus',
  REMOVE:           'Retirer',
  COVERED_LABEL:   (label) => `${label}, déjà inclus dans une notion choisie`,
  BREADCRUMB:      (labels) => labels.join(' › '),
    PRACTICE_LIMIT_LOADING: 'Chargement des règles de séance…',
    PRACTICE_LIMIT_ERROR: 'La limite de notions n’a pas pu être chargée.',
};

// The classes a teacher can browse in the chapter picker, in school order.
// ⚠ The labels are what a French teacher says out loud; the values are what
// `backend/config/grade_levels.py` calls them. One list, both used.
export const CLASS_BROWSE = [
  { value: 'CM1', label: 'CM1' },
  { value: 'CM2', label: 'CM2' },
  { value: '6e', label: '6ᵉ' },
  { value: '5e', label: '5ᵉ' },
  { value: '4e', label: '4ᵉ' },
  { value: '3e', label: '3ᵉ' },
  { value: '2nde', label: '2ᵈᵉ' },
  { value: '1ere', label: '1ʳᵉ' },
  { value: 'Terminale', label: 'Terminale' },
];

export const NOTION_CLASS_BROWSE = [
  { value: 'CP', label: 'CP' },
  { value: 'CE1', label: 'CE1' },
  { value: 'CE2', label: 'CE2' },
  ...CLASS_BROWSE,
];

export const GAME_ACTIVITIES = {
  TOOLBAR_LABEL:     'Actions des activités de jeu',
  IMPORT_CSV:        'Importer des activités depuis un fichier CSV',
  ADD:               'Ajouter',
  EDIT:              'Modifier',
  SAVE:              'Enregistrer',
  CANCEL:            'Annuler',
  DELETE_TITLE:      'Supprimer l’activité',
  DELETE_LABEL:      (activityName) => `Supprimer l’activité ${activityName}`,
};

export const DATA_TABLE = {
  SEARCH_PLACEHOLDER: 'Rechercher…',
  CLEAR_SEARCH:       'Effacer la recherche',
  RESET_FILTERS:      'Réinitialiser les filtres',
  RESET:              'Réinitialiser',
};
